import { ActionRef } from 'further-types/lp/action';
import { ActionReconciliationStatus } from 'further-types/lp/bank-reconciliation';
import { LpFeeCalculationBasis } from 'further-types/lp/lp-fee-template';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { CalendarDay } from 'further-ui/utils';

export type CurrencySettings = {
  currencyLcy: CurrencyCode;
  investorCurrency: CurrencyCode;
  exchangeRateLcyToInvestor: number;
  currencyFeedService: string;
};

export type CurrencyExchangedValue = {
  valueLcy: number;
  valueTcy: number;
};

export type OverviewDataPoint = CurrencyExchangedValue & {
  percentageOfTotal?: number;
};

export type OverviewData = {
  totalCommitment: OverviewDataPoint;
  paidInCapital: OverviewDataPoint;
  unfundedCommitment: OverviewDataPoint;
  liveCapitalOnAccount: OverviewDataPoint;
  capitalCommitmentLcy?: number;
  loanCommitmentLcy?: number;
  advanceSubscriptionBalanceLcy?: number;
  outsideOfCommitmentPaidInCapitalLcy: number;
  feeTemplate?: {
    id: string;
    name: string;
  };
  equalisationInterestFeeTemplate?: {
    id: string;
    name: string;
  };
  carriedInterestFeeTemplate?: {
    id: string;
    name: string;
  };
};

export type FundPerformanceMetric = {
  value: number;
  symbol: 'percentage' | 'multiple';
};

export type FundPerformanceData = {
  tvpi: CurrencyExchangedValue & FundPerformanceMetric;
  dpi: CurrencyExchangedValue & FundPerformanceMetric;
  rvpi: CurrencyExchangedValue & FundPerformanceMetric;
  grossMoic: FundPerformanceMetric;
  netMoic: FundPerformanceMetric;
  grossIrr: FundPerformanceMetric;
  netIrr: FundPerformanceMetric;
};

export type HoldingCommonData = {
  id: string;
  assetName: string;
  assetId: string;
  assetImage?: string;
  investmentDate: CalendarDay;
  investedCapitalLcy: number;
  grossMoic: number;
  grossIrr: number;
};

type LiveHolding = HoldingCommonData & {
  currentHoldingValueLcy: number;
};

type RealisedHolding = HoldingCommonData & {
  realisationValueLcy: number;
};

type HoldingByClass = {
  shareClassName: string;
};

type HoldingTotals = {
  investedCapitalLcy: number;
  grossMoic: number;
  grossIrr: number;
};

export type LiveHoldingByClass = LiveHolding &
  HoldingByClass & {
    proRatedHoldingUnits: number;
    initialUnitValueLcy: number;
    presentUnitValueLcy: number;
  };

export type LiveHoldingByHolding = LiveHolding & {
  classes: Array<LiveHoldingByClass>;
};

type LiveHoldingTotals = HoldingTotals & {
  currentHoldingValueLcy: number;
};

type RealisedHoldingTotals = HoldingTotals & {
  realisationValueLcy: number;
};

export type RealisedHoldingByClass = RealisedHolding &
  HoldingByClass & {
    realisationDate: CalendarDay;
    proRatedUnitsRealised: number;
    realisationValueLcy: number;
  };

export type RealisedHoldingByHolding = RealisedHolding & {
  realisationDate: CalendarDay;
  realisationValueLcy: number;
  classes: Array<RealisedHoldingByClass>;
};

export type CommitmentFee = {
  id: string;
  isFutureFee?: boolean;
  name: string;
  date: CalendarDay;
  calculationBasis: LpFeeCalculationBasis;
  valueExTaxLcy: number;
  salesTaxValueLcy: number;
  valueIncTaxLcy: number;
  status: string;
};

type CloseHistoryEntry = {
  id: string;
  date: CalendarDay;
  name: string;
  newCommitmentLcy: number;
  netEqualisationInterestPaidLcy: number;
};

export type IncomeAndExpenseEntry = {
  category: string;
  id: string;
  date: CalendarDay;
  action: string;
  valueLcy: number;
};

type CapitalBalanceAndReconciledPaymentsEntry = {
  id: string;
  date: CalendarDay;
  type: ActionRef;
  valueLcy: number;
  runningBalanceLcy: number;
};

export type PreferredReturnBalance = {
  outstandingPreferredReturnLcy: number;
  accruedPreferredReturnLcy: number;
  paidPreferredReturnLcy: number;
};

type SharedCapitalActionData = {
  id: string;
  date: CalendarDay;
  name: string;
};

type CapitalActionEntry = SharedCapitalActionData & {
  capitalCallLcy: number;
  capitalRefundLcy: number;
  netCallLcy: number;
  reconciliationStatus: ActionReconciliationStatus;
};

type FullCapitalActionEntryData = SharedCapitalActionData & {
  notionalPreCarryDistributionLcy: number;
  notionalCarriedInterestLcy: number;
  netDistributionInsideLcy: number;
  recallableDistributionLcy: number;
  netDistributionOutsideLcy: number;
  priorUnfundedCommitmentLcy: number;
  newUnfundedCommitmentLcy: number;
  unfundedCommitmentPercentage: number;
  insideCommitmentAmountLcy: number;
  outsideCommitmentAmountLcy: number;
  recalledCapitalLcy: number;
  equalisationInterestLcy: number;
  capitalRefundInsideCommitmentLcy: number;
  capitalRefundOutsideOfCommitmentLcy: number;
};

export type FullCapitalActionsTotals = Pick<
  FullCapitalActionEntryData,
  | 'insideCommitmentAmountLcy'
  | 'outsideCommitmentAmountLcy'
  | 'recalledCapitalLcy'
  | 'equalisationInterestLcy'
  | 'notionalPreCarryDistributionLcy'
  | 'notionalCarriedInterestLcy'
  | 'netDistributionInsideLcy'
  | 'netDistributionOutsideLcy'
  | 'recallableDistributionLcy'
  | 'capitalRefundInsideCommitmentLcy'
  | 'capitalRefundOutsideOfCommitmentLcy'
>;

export enum HoldingsSortBy {
  ByClass = 'byClass',
  ByHolding = 'byHolding',
}

export type CommitmentData = {
  id: string;
  vehicleId: string;
  isCarryPartnerCommitment: boolean;
  investorName: string;
  commitmentDate: CalendarDay;
  closeDate?: CalendarDay;
  earliestCloseDate?: CalendarDay;
  hasDocuments: boolean;
  currencySettings: CurrencySettings;
  overview: OverviewData;
  fundPerformance: FundPerformanceData;
  liveHoldings: {
    [HoldingsSortBy.ByClass]: {
      data: Array<LiveHoldingByClass>;
      totals: LiveHoldingTotals;
    };
    [HoldingsSortBy.ByHolding]: {
      data: Array<LiveHoldingByHolding>;
      totals: LiveHoldingTotals;
    };
  };
  realisedHoldings: {
    [HoldingsSortBy.ByClass]: {
      data: Array<RealisedHoldingByClass>;
      totals: RealisedHoldingTotals;
    };
    [HoldingsSortBy.ByHolding]: {
      data: Array<RealisedHoldingByHolding>;
      totals: RealisedHoldingTotals;
    };
  };
  feesAndCharges: Array<CommitmentFee>;
  futureFeesAndCharges: Array<CommitmentFee>;
  closeHistory: {
    data: Array<CloseHistoryEntry>;
    totals: {
      commitmentValueLcy: number;
      netEqualisationInterestLcy: number;
    };
  };
  incomeAndExpenses: Array<IncomeAndExpenseEntry>;
  capitalBalanceAndReconciledPayments: {
    data: Array<CapitalBalanceAndReconciledPaymentsEntry>;
    totals: {
      valueLcy: number;
    };
  };
  preferredReturnBalance: PreferredReturnBalance;
  capitalActions: {
    summary: Array<CapitalActionEntry>;
    fullDetails: {
      data: Array<FullCapitalActionEntryData>;
      totals: FullCapitalActionsTotals;
    };
  };
};
